import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login,
  AdminPanelSettings,
} from '@mui/icons-material';

interface LoginFormProps {
  onLogin: (username: string, password: string) => void;
  error?: string;
  loading?: boolean;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLogin, error, loading }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (username.trim() && password.trim()) {
      onLogin(username.trim(), password);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={20}
        sx={{
          p: 6,
          maxWidth: 450,
          width: '100%',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '25px',
          border: '1px solid rgba(255,255,255,0.3)',
          boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          }
        }}
        className="floating"
      >
        <Box textAlign="center" mb={4}>
          <Box
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              borderRadius: '50%',
              width: 80,
              height: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            }}
            className="pulse"
          >
            <AdminPanelSettings sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 1,
            }}
          >
            🔐 Admin Girişi
          </Typography>
          
          <Typography variant="body1" color="text.secondary">
            Bot yönetim paneline erişim için giriş yapın
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                fontSize: '24px'
              }
            }}
          >
            ❌ {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="👤 Kullanıcı Adı"
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '15px',
                background: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#764ba2',
                    borderWidth: '2px',
                  }
                }
              },
              '& .MuiInputLabel-root': {
                fontWeight: 'bold',
                '&.Mui-focused': {
                  color: '#764ba2',
                }
              }
            }}
          />

          <TextField
            fullWidth
            label="🔑 Şifre"
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    disabled={loading}
                    sx={{
                      color: '#667eea',
                      '&:hover': {
                        color: '#764ba2',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 4,
              '& .MuiOutlinedInput-root': {
                borderRadius: '15px',
                background: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                    borderWidth: '2px',
                  }
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#764ba2',
                    borderWidth: '2px',
                  }
                }
              },
              '& .MuiInputLabel-root': {
                fontWeight: 'bold',
                '&.Mui-focused': {
                  color: '#764ba2',
                }
              }
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading || !username.trim() || !password.trim()}
            startIcon={<Login />}
            sx={{
              py: 2,
              borderRadius: '15px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              textTransform: 'none',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #764ba2, #667eea)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(102, 126, 234, 0.6)',
              },
              '&:disabled': {
                background: '#ccc',
                color: '#999',
                transform: 'none',
                boxShadow: 'none',
              },
              transition: 'all 0.3s ease',
            }}
            className="modern-button"
          >
            {loading ? '🔄 Giriş Yapılıyor...' : '🚀 Giriş Yap'}
          </Button>
        </form>

        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            Bot Management Panel v1.0
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginForm;
